import type { Localized } from "../i18n";

/**
 * Every line Turo speaks in L2 and L3 — plan §7.
 *
 * 🔴 NAMESPACED SEMANTICALLY, NOT BY LEVEL. §7: *"so content can move between
 * levels without renaming."* `core.*` is the shared frame, `body.*` is autonomy
 * and safety, `greet.*` is greetings and courtesy. A line that moves from L3 S2
 * to L2 S3 keeps its id and its recording.
 *
 * 🔴 CONTENT-ADDRESSED: IDENTICAL LINES SHARE ONE RECORDING ID. `core.greet`
 * opens all eight sessions and is recorded once.
 *
 * ⚠️ RECORDING DIRECTION, AND IT IS NOT A STYLE NOTE. §7: *"The celebration
 * lines must be INDISTINGUISHABLE IN ENERGY from L1's — a three-year-old reads
 * tone perfectly and hears any softening as failure. The safety lines are gentle
 * and matter-of-fact, never grave; gravity is what makes a child anxious about a
 * topic."* Same artist, same session, same mic as L1.
 *
 * 🔴 EVERY WORD HERE IS CHECKED BY `check-turo-safety.mjs` RULE 11. The banned
 * list — scared, afraid, bad touch, good touch, stranger, wrong, try again,
 * danger, hurt you — is not squeamishness. §2: unsafe touch *"usually does not
 * FEEL bad… a child taught to watch for 'bad' touch has a detector that won't
 * fire in the situation that actually occurs"*, and *"saying 'stranger' teaches
 * a child to relax around exactly the people who present risk."*
 */

/* ─────────────────────────────── core · 12 lines, §7 ── */

/**
 * ⚠️ RECORDED ONCE AND USED EVERYWHERE. These twelve carry both levels' frame.
 */
export const CORE_LINES: Readonly<Record<string, Localized<string>>> = {
  "core.greet": { en: "Hello! It's Turo. Let's play together." },
  "core.consent.ask": {
    en: "I would love to watch you move. Is that okay? You can say no. No is always okay.",
  },
  "core.consent.yes": { en: "Thank you for saying yes! Let's go." },
  "core.consent.no": {
    en: "That's okay. Thank you for telling me. I'll show you, and you can copy me all by yourself.",
  },
  "core.copyme": { en: "Copy me!" },
  "core.watchme": { en: "Watch this one." },
  "core.celebrate.1": { en: "Yes! Beautiful." },
  "core.celebrate.2": { en: "That's it! You are so strong." },
  "core.celebrate.3": { en: "Wonderful!" },
  "core.celebrate.4": { en: "Look at you go!" },
  "core.bye": { en: "That was lovely. See you next time!" },
  "core.nocamera": {
    en: "I can't see you right now, but that's alright. Copy me anyway!",
  },
};

/* ──────────────────── body · autonomy and safety, §7 ── */

/**
 * 🔴 THE LINES TO RECORD WITH MOST CARE — §7 says so by name. Several are the
 * only thing a child carries out of the room.
 */
export const BODY_LINES: Readonly<Record<string, Localized<string>>> = {
  "body.s1.open": { en: "Today we say hello. There are lots of ways!" },
  "body.s1.choose": { en: "How do you want to say hello today? You choose!" },
  "body.s1.close": { en: "You said hello in so many ways. That was perfect." },

  "body.s2.open": { en: "Today we talk about my body." },
  "body.boss.cue": { en: "Hands on your hips!" },
  "body.boss.concept": { en: "I am the boss of my own body." },
  "body.mine.cue": { en: "Fold your arms like this." },
  "body.mine.concept": { en: "My body is mine. It belongs to me." },
  /**
   * ⚠️ OPTIONAL, SCHOOL-ENABLED, DEFAULT OFF — §9 decision 9. It is the deepest
   * part of the curriculum and it is ONE VERBAL LINE, never drawn (§2). The
   * privacy is carried by the crossed arms and the stop gesture, never by
   * pointing at anatomy.
   */
  "body.clothes": {
    en: "The parts under my clothes are private. They belong to me.",
  },
  "body.selfhug.cue": { en: "Give yourself a big hug." },
  "body.selfhug.concept": { en: "I can look after me." },
  "body.thatsme.cue": { en: "Point at yourself!" },
  "body.thatsme.concept": { en: "That's me. I like being me." },
  "body.s2.close": { en: "Your body is yours. Always." },

  "body.s3.open": { en: "Today we talk about touches that feel nice." },
  "body.b1.concept": { en: "A high five feels good. That is a safe touch." },
  "body.hug.concept": { en: "A hug from someone I love feels warm and happy." },
  "body.hands.concept": { en: "Holding hands keeps me safe when we walk." },
  "body.thumbs.cue": { en: "Two thumbs up!" },
  "body.thumbs.concept": { en: "Safe touches make me feel warm and happy." },
  "body.teacher.concept": { en: "Well done! Kind words feel good too." },
  "body.nothankyou.cue": { en: "One hand up. No thank you!" },
  "body.nothankyou.concept": {
    en: "I can say no thank you. That is always okay, and I can still be kind.",
  },
  "body.no.cue": { en: "Both hands up. No!" },
  /**
   * 🔴 "I DON'T HAVE TO SHOUT" IS THE LINE, AND IT IS DELIBERATE. Asset pack §4:
   * a shouting NO *"teaches that refusing requires working yourself up to it.
   * Calm-and-certain is something a four-year-old can actually produce in a real
   * situation; angry-shouting they can only produce in a game."*
   */
  "body.no.concept": {
    en: "If I don't want something, I can say no. I don't have to shout. I just have to mean it.",
  },
  "body.s3.close": { en: "Nice touches feel warm. And no is always okay." },

  "body.s4.open": { en: "Today we listen to our tummy." },
  "body.uhoh.cue": { en: "Put your hands on your tummy." },
  "body.uhoh.concept": {
    en: "Sometimes my tummy feels uh-oh. That is my tummy telling me something.",
  },
  "body.safe.cue": { en: "Hands on your heart." },
  "body.safe.concept": { en: "And sometimes it feels warm and easy. That is my safe feeling." },
  "body.five.cue": { en: "Hold up five fingers!" },
  "body.five.concept": { en: "These are my five people. I can tell them anything." },
  "body.tell.concept": { en: "And I tell. Telling is always the right thing to do." },
  /**
   * 🔴 THE TWO LINES A CHILD CARRIES OUT OF THE ROOM — §3.6. The second exists
   * because first disclosures get brushed off, and a child who tries once and is
   * dismissed usually doesn't try again.
   */
  "body.notfault": { en: "If something happens, it is never your fault. Never, never." },
  "body.keeptelling": {
    en: "And if the first person is busy, tell somebody else. Keep telling until somebody listens.",
  },
  "body.s4.close": { en: "You know your tummy and you know your people. Well done." },
};

/* ────────────────── greet · greetings and courtesy, §7 ── */

export const GREET_LINES: Readonly<Record<string, Localized<string>>> = {
  "greet.s1.open": { en: "Today we learn kind words, and what to say from morning to night." },
  "greet.wave.cue": { en: "Wave hello! Hello, hello!" },
  "greet.wave.concept": { en: "A wave says hello without any words." },
  "greet.namaste.cue": { en: "Hands together. Namaste!" },
  /*
   * ⚠️ MISSING UNTIL THE TRANSCRIPT WAS GENERATED. L2 S1 beat 4 names
   * `greet.namaste.concept` and the table had no such line — so the beat spoke
   * the cue, then silence where the teaching sentence belongs. The gap is
   * invisible in code review and obvious the moment the script is read aloud,
   * which is the argument for generating the transcript at all.
   */
  "greet.namaste.concept": {
    en: "Namaste is how we say hello to grown-ups.",
  },
  /**
   * 🔴 NAMASTE IS FIRST-CLASS, NOT A FOOTNOTE — §4.3, said to the child
   * directly: it works for hello and goodbye, morning and night, with anyone.
   */
  "greet.namaste.anytime": {
    en: "Namaste works for hello and for goodbye. Morning, night, anyone. It is never wrong.",
  },
  "greet.highfive.cue": { en: "Up high! High five!" },
  "greet.highfive.concept": { en: "A high five says hello and well done." },
  "greet.handshake.cue": { en: "Put your hand out to say hello." },
  "greet.wavebye.cue": { en: "Wave goodbye! Bye bye!" },
  /** The surprise the session is built on — §4.3. */
  "greet.same.hands": {
    en: "Look! Hello and goodbye are the same hands. Only the words are different.",
  },
  "greet.kiss.cue": { en: "Send a kiss! Mwah!" },
  "greet.s1.close": { en: "The hands are easy. The words are the fun part!" },

  "greet.s2.open": { en: "Today we learn the kind words." },
  "greet.thankyou.cue": { en: "Open your hands. Thank you!" },
  "greet.sorry.cue": { en: "Hand on your chest. Sorry." },
  /**
   * 🔴 THE REPAIR HALF — VALIDATOR RULE 20, §4.4. *"The known failure mode of
   * teaching apology to preschoolers is they learn to PERFORM it — the word
   * becomes a token that ends a conversation rather than fixing anything."*
   * Three-year-olds can say both halves.
   */
  "greet.sorry.repair": { en: "And then we ask: are you okay? That is the important part." },
  "greet.excuseme.cue": { en: "One finger up. Excuse me!" },
  /** §4.4: sorry = I did something. Excuse me = I need to pass or interrupt. */
  "greet.excuseme.notsorry": {
    en: "Excuse me is for when I need to get past, or when I want to say something.",
  },
  "greet.please.cue": { en: "Hands together. Please can I have it?" },
  "greet.nothankyou.cue": { en: "One hand up. No thank you!" },
  /** §1: the bridge between the levels — a refusal that is also courteous. */
  "greet.nothankyou.bridge": {
    en: "No thank you is a kind word too. I can say no and still be kind.",
  },
  "greet.s2.close": { en: "Kind words are easy when you know them!" },

  "greet.s3.open": { en: "Today we say hello all day long." },
  "greet.morning.cue": { en: "Big stretch! Good morning!" },
  "greet.afternoon.cue": { en: "Shade your eyes. Good afternoon!" },
  "greet.evening.cue": { en: "Cup your hands, like a little light. Good evening!" },
  "greet.night.cue": { en: "Hands beside your cheek. Good night!" },
  /** §4.4: children conflate all four and then say good night on arrival. */
  "greet.s3.nightisgoodbye": {
    en: "Good night is a goodbye, not a hello. We say it when we are going to sleep.",
  },
  "greet.s3.close": { en: "Morning, afternoon, evening, night. You know them all!" },

  "greet.s4.open": { en: "Now let's find the right words together." },
  "greet.s4.close": { en: "You found the right words. Wonderful!" },
};

/* ────────────────── L2 scenarios · the founder's script ── */

/**
 * 🔴 THE AFFIRMATIONS NAME WHAT THE CHILD CHOSE, AND THEY ARE EQUAL IN WARMTH.
 * §3.3: *"Turo's warmth is identical for every choice including the same one
 * every single time. The lesson is that the choice is theirs and it is
 * honoured."* A hug and a wave get the same delight; so do "yes, share" and
 * "not yet". The moment one option sounds better, the beat stops being a choice.
 */
export const L2_SCENARIO_LINES: Readonly<Record<string, Localized<string>>> = {
  /* ── what each scenario teaches ── */

  /**
   * 🔴 ONE THING PER SCENARIO, STATED AS A FACT. With nothing to tap, offering
   * alternatives aloud would be asking a question the child cannot answer —
   * worse than not asking. So Turo says what we do, and then does it.
   */
  "sc.teach.friend": { en: "When a friend comes to say hello, we can wave!" },
  /**
   * ⚠️ THE TEACHER IS GREETED WITH THE WORDS SHE USED, NOT WITH NAMASTE.
   * She has just said "good morning" — answering a greeting with the SAME
   * greeting is the first turn-taking a child learns, and it is what actually
   * happens at a school gate. Namaste belongs to the family elders below.
   */
  "sc.teach.teacher": {
    en: "When our teacher says good morning, we wave and say good morning back!",
  },
  /**
   * 🔴 AMMA AND APPA BOTH GET NAMASTE — the founder's change. In an Indian home
   * the greeting a child is taught for their parents is the same one they are
   * taught for grandparents, and teaching a different gesture for each would
   * make the rule harder, not warmer.
   *
   * ⚠️ THE HUG DOES NOT DISAPPEAR, IT MOVES INTO THE CLOSE. §3.3 keeps affection
   * in this level deliberately — *"a child who wants a hug must be able to
   * choose one, or the level teaches that affection is suspect"* — so the hug is
   * still named, and named as the child's to choose.
   */
  "sc.teach.mother": {
    en: "When we see Amma, we can open our arms for a big hug!",
  },
  "sc.teach.father": {
    en: "When Appa comes home, we put our hands together and say Namaste.",
  },
  "sc.teach.elder": {
    en: "When we meet Grandma or Grandpa, we put our hands together and say Namaste.",
  },
  "sc.teach.help": {
    en: "When we need help, we put our hand up and say, can you help me?",
  },
  "sc.teach.toy": {
    en: "If we are still using them, we can hold up our hand and say, not yet.",
  },
  "sc.teach.final": { en: "When someone we know says hello, we can wave back!" },

  /**
   * ── "now do it with me" ────────────────────────────────────────────────────
   *
   * 🔴 CHOOSING IS NOT LEARNING. A child who taps "Namaste" has expressed a
   * preference; they have not greeted anyone. The gesture only becomes theirs
   * once they have made it, so after every choice Turo performs it and asks them
   * to join in — and then WAITS.
   *
   * ⚠️ THE PAUSE IS THE POINT AND IT IS NOT DEAD TIME. A four-year-old needs a
   * beat to work out what their hands are doing; filling that silence with more
   * narration is the commonest way an adult accidentally answers their own
   * question. Nothing is measured during it — §6 keeps voice one-way and there
   * is no camera — so the wait is a real invitation rather than an assessment.
   */
  "sc.do.wave": { en: "Now do it with me. Wave your hand… hello!" },
  "sc.do.namaste": { en: "Now do it with me. Hands together… Namaste!" },
  "sc.do.highfive": { en: "Now do it with me. Hand up high… high five!" },
  "sc.do.hello": { en: "Now say it with me… hello!" },
  "sc.do.goodmorning": {
    en: "Now do it with me. Wave your hand and say… good morning!",
  },
  "sc.do.hug": { en: "Now do it with me. Open your arms wide… a big hug!" },
  /**
   * ⚠️ THE HAND AND THE WORDS TOGETHER. Turo is performing a raised hand, so an
   * invitation that names only the sentence leaves the child copying half the
   * lesson — and the raised hand is the part a teacher across a busy room
   * actually sees.
   */
  "sc.do.askhelp": {
    en: "Now do it with me. Put your hand up and say… can you help me?",
  },
  "sc.do.handup": { en: "Now do it with me. Put your hand up!" },
  "sc.do.share": { en: "Now say it with me… yes, let's share!" },
  "sc.do.notyet": { en: "Now say it with me… not yet." },

  /** Said after the pause, however the child got on. Never a verdict. */
  "sc.do.done": { en: "Lovely. You did that with me." },

  /* ── 1 · a friend ── */
  "sc.friend.open": { en: "Look! Your friend is coming to say hello." },
  "sc.friend.close": { en: "You waved all by yourself. That was a lovely hello!" },

  /* ── 2 · your teacher ── */
  "sc.teacher.open": { en: "Imagine you are coming to school and you see your teacher." },
  "sc.teacher.says": { en: "Your teacher smiles and says, good morning!" },
  "sc.teacher.close": { en: "Excellent! You greeted your teacher so politely." },

  /* ── 3 · your mother ── */
  "sc.mother.open": { en: "Now imagine you are at home with Amma." },
  "sc.mother.says": { en: "She smiles and says, hello!" },
  /**
   * 🔴 THE LINE THE WHOLE LEVEL IS FOR. It is said after ANY choice, not only
   * after the hug — a child who waved must hear it too, because the lesson is
   * that the choice existed, not that they used it.
   */
  /**
   * 🔴 THE PERMISSION IS ATTACHED TO THE HUG, NOT OFFERED INSTEAD OF IT. §3.3
   * keeps affection in this level on purpose — *"a child who wants a hug must be
   * able to choose one, or the level teaches that affection is suspect"* — and
   * §1 requires the other half be said just as plainly.
   */
  "sc.mother.close": {
    en: "Hugs are special. You can always choose whether you want one, and it is always okay to say no thank you.",
  },

  /* ── 4 · your father ── */
  "sc.father.open": { en: "Now imagine Appa comes home from work." },
  "sc.father.says": { en: "He says, hello!" },
  "sc.father.close": { en: "Wonderful! You greeted Appa all by yourself!" },

  /* ── 5 · an elder ── */
  "sc.elder.open": { en: "Imagine you meet Grandma, Grandpa, or another elder." },
  "sc.elder.says": { en: "They smile and say hello." },
  "sc.elder.close": { en: "You were respectful and kind. Well done!" },

  /* ── 6 · asking for help ── */
  "sc.help.open": {
    en: "Imagine your shoelace is open, and you cannot tie it.",
  },
  "sc.help.ask": {
    en: "You can put your hand up. Or you can say, can you help me?",
  },
  "sc.say.askhelp": { en: "Wonderful! Ma'am, can you help me? That is exactly right." },
  "sc.say.handup": { en: "Wonderful! You put your hand up. That is exactly right." },
  "sc.help.close": {
    en: "Remember, asking for help is okay. You do not have to do everything by yourself.",
  },

  /* ── 7 · a friend wants your toy ── */
  "sc.toy.open": { en: "Imagine you are drawing, and your friend asks, can I use your crayons?" },
  "sc.toy.ask": {
    en: "You can say yes, and share. Or you can say not yet, if you are still playing.",
  },
  "sc.say.share": { en: "Lovely! You said yes and shared. That was kind." },
  /**
   * ⚠️ EXACTLY AS WARM AS SHARING — validator rule 1's spirit. If "not yet"
   * sounded even slightly disappointed, the level would be teaching that a
   * refusal costs you Turo's approval, which is the belief §1 exists to prevent.
   */
  "sc.say.notyet": { en: "Lovely! You said not yet. That was your choice to make." },
  "sc.toy.close": {
    en: "Not yet is a kind answer too. You can always use your words to say how you feel.",
  },

  /* ── 8 · you choose ── */
  "sc.final.open": {
    en: "Now it is your turn. Imagine someone you know comes to say hello.",
  },
  "sc.final.close": { en: "You waved hello all by yourself! That was wonderful." },
  "sc.final.praise": {
    en: "You are learning how to be friendly, respectful, and confident. Great job!",
  },
};

/* ─────────────────────── L3 lessons · the right words ── */

/**
 * ⚠️ EVERY SORRY USES THE PAIRED FORM — validator rule 20. §4.4: apology taught
 * alone becomes a token that ends a conversation rather than fixing anything.
 * Three-year-olds can say both halves.
 */
export const L3_LESSON_LINES: Readonly<Record<string, Localized<string>>> = {
  /* ── thank you ── */
  "l3.thanks.open": { en: "Look! Nani has brought you a present." },
  "l3.teach.thanks": {
    en: "When someone gives us something, we open our hands and say, thank you!",
  },
  "l3.do.thanks": { en: "Now say it with me… thank you!" },
  "l3.thanks.close": { en: "Thank you is a lovely word. It makes people happy." },

  /* ── sorry ── */
  "l3.sorry.open": { en: "Oh! You bumped into your friend by mistake." },
  "l3.teach.sorry": {
    en: "When we bump someone, we put a hand on our chest and say, sorry. Are you okay?",
  },
  "l3.do.sorry": { en: "Now say it with me… sorry. Are you okay?" },
  /**
   * 🔴 THE REPAIR HALF IS THE LESSON, NOT THE WORD. §4.4: a child who learns
   * only "sorry" learns to perform it; asking "are you okay?" is what makes it
   * mean something.
   */
  "l3.sorry.close": {
    en: "Sorry has two parts. We say sorry, and then we ask, are you okay?",
  },

  /* ── excuse me ── */
  "l3.excuse.open": { en: "Your friend is in the doorway, and you want to get past." },
  "l3.teach.excuse": {
    en: "When we need to get past, we put one finger up and say, excuse me.",
  },
  "l3.do.excuse": { en: "Now say it with me… excuse me!" },
  /** §4.4: sorry = I did something. Excuse me = I need to pass or interrupt. */
  "l3.excuse.close": {
    en: "Sorry is for when we did something. Excuse me is for when we need to get past.",
  },

  /* ── please ── */
  "l3.please.open": { en: "Your friend has the red crayon, and you would like a turn." },
  "l3.teach.please": {
    en: "When we want something, we hold our hands out and say, please may I have it?",
  },
  "l3.do.please": { en: "Now say it with me… please may I have it?" },
  "l3.please.close": { en: "Please is how we ask. It is a kind way to want something." },

  /* ── no thank you ── */
  "l3.nothanks.open": { en: "Amma is offering you a sweet, but you do not want one." },
  "l3.teach.nothanks": {
    en: "When we do not want something, we can put one hand up and say, no thank you.",
  },
  "l3.do.nothanks": { en: "Now say it with me… no thank you!" },
  /**
   * 🔴 §1's BRIDGE, SAID PLAINLY. A refusal that is also courteous — and the
   * courtesy never makes the refusal optional.
   */
  "l3.nothanks.close": {
    en: "No thank you is a kind word too. We can say no and still be kind.",
  },

  /* ── all day long ── */
  "l3.morning.open": { en: "The sun is coming up, and you are going to school." },
  "l3.teach.morning": {
    en: "In the morning we stretch up big and say, good morning!",
  },
  "l3.do.morning": { en: "Now say it with me… good morning!" },
  "l3.morning.close": { en: "Good morning is for when the day is starting." },

  "l3.afternoon.open": { en: "Now the sun is high up in the sky. It is lunch time." },
  "l3.teach.afternoon": {
    en: "In the afternoon we shade our eyes from the sun and say, good afternoon!",
  },
  "l3.do.afternoon": { en: "Now say it with me… good afternoon!" },
  "l3.afternoon.close": { en: "Good afternoon is for the middle of the day." },

  "l3.evening.open": { en: "The sun is going down, and Appa is coming home." },
  "l3.teach.evening": {
    en: "In the evening we cup our hands like a little light and say, good evening!",
  },
  "l3.do.evening": { en: "Now say it with me… good evening!" },
  "l3.evening.close": { en: "Good evening is for when the sun goes down." },

  "l3.night.open": { en: "It is dark now, and it is time for bed." },
  "l3.teach.night": {
    en: "At night we rest our hands beside our cheek and say, good night!",
  },
  "l3.do.night": { en: "Now say it with me… good night!" },
  /** §4.4: children conflate all four and then say good night on arrival. */
  "l3.night.close": {
    en: "Good night is a goodbye, not a hello. We say it when we are going to sleep.",
  },

  /* ── goodbye ── */
  "l3.bye.open": { en: "Your friend is going home now." },
  "l3.teach.bye": { en: "When someone goes home, we wave and say, bye bye!" },
  "l3.do.bye": { en: "Now do it with me. Wave your hand… bye bye!" },
  /** §4.3's surprise, said to the child directly. */
  "l3.bye.same": {
    en: "Look! Hello and goodbye are the same hands. Only the words are different.",
  },
  "l3.bye.close": { en: "Now you know how to say goodbye." },
};

export const TURO_L2_L3_LINES: Readonly<Record<string, Localized<string>>> = {
  ...CORE_LINES,
  ...BODY_LINES,
  ...GREET_LINES,
  ...L2_SCENARIO_LINES,
  ...L3_LESSON_LINES,
};

export const TURO_L2_L3_LINE_IDS = Object.keys(TURO_L2_L3_LINES);

/**
 * The audio key a voice-line id resolves to.
 *
 * 🔴 IDS ARE DOTTED, FILENAMES ARE FLAT. §7 namespaces lines as `body.s1.open`
 * so content can move between levels without renaming; `public/shared/audio/{lang}/`
 * holds flat `.mp3` files, and `check-audio-manifest.mjs` pairs the two. One
 * deterministic transform keeps the two spaces in step without a lookup table
 * that could drift out of date.
 */
export const audioKeyFor = (lineId: string): string =>
  `vo_turo_${lineId.replace(/\./g, "_")}`;
